import './AddBalance.scss';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setLastTimeOfAddBalance } from '../../storeRedux';
import {showAlert} from '../../components/AlertBox/AlertBox';
import Pagination from '../../components/Layout/Pagination';
import PageToolbar from '../../components/Layout/PageToolbar';
import CopyButton from '../../components/CopyButton/CopyButton';

const STATUS_MAP = {
    '1': { label: 'Ожидает', color: '#f59e0b' },
    '2': { label: 'Получен', color: '#22c55e' },
    '3': { label: 'Ошибка', color: '#ef4444' },
    '4': { label: 'Обработка', color: '#3b82f6' },
};


export default function AddBalance() {
    const { t } = useTranslation('balance');
    const dispatch = useDispatch();
    const theme = useSelector((state) => state.APP_THEME);
    const api_url_no_ra = useSelector((state) => state.API_URL_NO_RAN);
    const api_url = useSelector((state) => state.API_URL);

    const jwtToken = localStorage.getItem('accessToken');
    const navigate = useNavigate();

    const [topUpBalanceAddress, setTopUpBalanceAddress] = useState('');
    const [walletStatus, setWalletStatus] = useState(null);
    const [acknowledging, setAcknowledging] = useState(false);
    const [noticeError, setNoticeError] = useState(false);

    const fetchWalletStatus = useCallback(async () => {
        try {
            const response = await fetch(`${api_url}wallet-rotation/`, {
                headers: { 'Authorization': `JWT ${jwtToken}` }
            });
            if (!response.ok) throw new Error('wallet status unavailable');
            setWalletStatus(await response.json());
        } catch (_) {
            // Fail closed: history must not advertise an unverified current QR.
            setWalletStatus(previous => previous ? { ...previous, current_address: null } : null);
        }
    }, [api_url, jwtToken]);

    useEffect(() => {
        fetchWalletStatus();
        const timer = setInterval(fetchWalletStatus, 30000);
        return () => clearInterval(timer);
    }, [fetchWalletStatus, topUpBalanceAddress]);

    const acknowledgeRotation = async () => {
        if (acknowledging || !walletStatus?.notice) return;
        setAcknowledging(true);
        setNoticeError(false);
        try {
            const response = await fetch(`${api_url}wallet-rotation/${walletStatus.notice.id}/ack/`, {
                method: 'POST', headers: { 'Authorization': `JWT ${jwtToken}` }
            });
            if (!response.ok) throw new Error('acknowledgment failed');
            await fetchWalletStatus();
        } catch (_) {
            setNoticeError(true);
        } finally {
            setAcknowledging(false);
        }
    };

    const setLastTime = (some_data) => {
        const newData = some_data;
        dispatch(setLastTimeOfAddBalance(newData));
    };

    useEffect(() => {
        if (!jwtToken) {
            navigate('/auth');
            localStorage.clear();
        }
    }, [jwtToken, navigate]);

    const [paymentsData, setPaymentsData] = useState([]);

    const [pageSize, setPageSize] = useState(4);
    const [pageIndex, setPageIndex] = useState(1);
    const [reqRowsCount, setReqRowsCount] = useState(0);

    const fetchData = useCallback(async () => {
        try {
            const response = await fetch(`${api_url}exchange/userbalancedeposit/info?page=${pageIndex}&page_size=${pageSize}`, {
                method: 'GET',
                headers: {
                    'Authorization': `JWT ${jwtToken}`
                }
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            setPaymentsData(data.results);
            setReqRowsCount(data.count);
        } catch (error) {
            // console.error('Fetch error:', error);
        }
    }, [api_url, pageIndex, pageSize, jwtToken]);

    useEffect(() => {
        fetchData();
    }, [fetchData, topUpBalanceAddress]);



    function formatDate(inputDate) {
        const date = new Date(inputDate);

        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${hours}:${minutes} [${day}/${month}/${year}]`;
    }

    const createAddBalance = async () => {
        const now = new Date();

        try {
            const response = await fetch(`${api_url_no_ra}api/v3/user_balance_deposit/create/`, {
                method: 'POST',
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (response.status === 201) {
                showAlert('success', t('balance:successWallet', { address: data.address }));
                setTopUpBalanceAddress(data.address);
                setLastTime(now.toISOString());
            } else if (response.status === 400) {
                showAlert('error', data.message);
            } else if (response.status === 401) {
                showAlert('error', data.message);
            }
        } catch (error) {
            throw new Error(t('balance:errorOccurred', { message: error.message }));
        }
    };

    const getStatusInfo = (statusId) => {
        return STATUS_MAP[statusId] || { label: statusId, color: '#6b7280' };
    };

    const isPending = (statusId) => ["1", "4"].includes(statusId);

    return (
        <div className={theme === 'light' ? "AddBalance" : "AddBalance dark"}>
            <div className="content">
                {walletStatus?.notice && (
                    <div className="add-balance-alert" role="status">
                        <p><strong>{t('balance:rotationTitle')}</strong></p>
                        <p>{t('balance:rotationMessage')}</p>
                        {walletStatus.current_address && (
                            <p style={{ overflowWrap: 'anywhere' }}>
                                {t('balance:currentAddress')}: {walletStatus.current_address}
                                <CopyButton text={walletStatus.current_address} />
                            </p>
                        )}
                        <button className="confirmation-modal-button cancel" type="button" disabled={acknowledging} onClick={acknowledgeRotation}>
                            {t('balance:rotationAcknowledge')}
                        </button>
                        {noticeError && <p role="alert">{t('balance:rotationAckError')}</p>}
                    </div>
                )}
                <PageToolbar
                    onAdd={createAddBalance}
                    addLabel={t('balance:topUp')}
                />
                <Pagination
                    pageIndex={pageIndex}
                    totalCount={reqRowsCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[4, 8, 12]}
                />
                <div className="add-balance-alert">
                    <p><strong>{t('balance:attention')}</strong> {t('balance:createRequestForEachDeposit')}</p>
                    <p>{t('balance:pressButtonForNewDeposit')} <strong>{t('balance:topUpButton')}</strong>.</p>
                </div>
                <div className="balance-cards-grid">
                    {paymentsData?.map((item, index) => {
                        const statusId = item.status_id?.value;
                        const status = getStatusInfo(statusId);
                        const pending = isPending(statusId);
                        const isTron = (item.kind?.value || 'USDT').toUpperCase() === 'USDT';
                        const historical = isTron && walletStatus?.retired_addresses?.includes(item.address?.value);
                        const current = !isTron || Boolean(
                            walletStatus?.current_address && !historical
                            && item.address?.value === walletStatus.current_address
                        );

                        return (
                            <div className="balance-card" key={index}>
                                <div className="balance-card__header">
                                    <span className="balance-card__badge" style={{ background: status.color }}>
                                        {t('balance:statusMap.' + statusId)}
                                    </span>
                                    <span className="balance-card__currency">
                                        {(item.kind?.value || 'USDT').toUpperCase()}
                                    </span>
                                </div>

                                {pending && current && item.qrcode?.value && (
                                    <div className="balance-card__qr">
                                        <img src={item.qrcode.value} alt="QR" />
                                    </div>
                                )}

                                <div className="balance-card__address">
                                    <span className="balance-card__address-label">{t(historical ? 'balance:historicalAddress' : 'balance:walletAddress')}</span>
                                    <div className="balance-card__address-row">
                                        <span className="balance-card__address-value">{item.address?.value}</span>
                                        {current && <CopyButton text={item.address?.value || ''} />}
                                    </div>
                                </div>

                                <div className="balance-card__amount">
                                    {item.amount?.value ? (
                                        <span className="balance-card__amount-value">
                                            {item.amount.value} <small>{(item.kind?.value || '').toUpperCase()}</small>
                                        </span>
                                    ) : (
                                        <span className="balance-card__amount-pending">{t('balance:awaitingDeposit')}</span>
                                    )}
                                </div>

                                <div className="balance-card__dates">
                                    <div className="balance-card__date-row">
                                        <span className="balance-card__date-label">{t('balance:created')}</span>
                                        <span className="balance-card__date-value">
                                            {item.created?.value ? formatDate(item.created.value) : '-'}
                                        </span>
                                    </div>
                                    <div className="balance-card__date-row">
                                        <span className="balance-card__date-label">{t('balance:credited')}</span>
                                        <span className="balance-card__date-value">
                                            {item.amount_at?.value ? formatDate(item.amount_at.value) : '-'}
                                        </span>
                                    </div>
                                </div>

                            </div>
                        );
                    })}
                </div>
                <Pagination
                    pageIndex={pageIndex}
                    totalCount={reqRowsCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[4, 8, 12]}
                />
            </div>
        </div>
    )
}
